import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { apiRequest } from "src/async/apiUtils";
import Button from "src/components/form/Button";
import Input from "src/components/form/Input";
import Preloader from "src/components/shared/LoadingAnimations/Preloader";
import { SiteContext } from "src/context/site";
import AuthDataLayout from "src/layouts/AuthDataLayout";
import auth from "src/services/auth";

const Login = () => {
  const navigate = useNavigate();

  const { site } = useContext(SiteContext);

  const [lockout, setLockout] = useState(0);
  const [disabled, setDisabled] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [intervalId, setIntervalId] = useState(null);
  const [requireTwoFactor, setRequireTwoFactor] = useState(false);
  const [loadingTrackLogin, setLoadingTrackLogin] = useState(true);
  const [loadingSsos, setLoadingSsos] = useState(true);
  const [ssos, setSsos] = useState([]);

  const loadSso = useCallback(() => {
    (async () => {
      if (!site?._id) return;

      setLoadingSsos(true);

      try {
        const { data: resData } = await apiRequest("GET", "/sso/default-list");

        // If not home route set the loader to false
        if (!resData.data.length || !site.sso_default_relay_url || !["/"].includes(window.location.pathname)) {
          setLoadingSsos(false);
        }

        setSsos(resData.data);
      } catch (err) {
        toast.error(err.message);
        setLoadingSsos(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [site?._id]);

  const manageLockout = (data, intervalId) => {
    setLockout(data?.timer || 60);
    if (intervalId) clearInterval(intervalId);

    const intervalDataId = setInterval(() => {
      setLockout((cur) => {
        if (cur) {
          return cur - 1;
        } else {
          clearInterval(intervalDataId);
          return cur;
        }
      });
    }, 1000);

    setIntervalId(intervalDataId);
  };

  useEffect(() => {
    const trackLogin = async () => {
      setLoadingTrackLogin(true);

      try {
        const { data: resData } = await apiRequest("post", "/portal/login/tracking", {
          body: { fromPortal: true },
        });

        if (resData.status === 200 && resData?.data?.attemp_count > 10 && resData?.data?.timer !== 0) {
          manageLockout(resData.data);
        }
      } catch (err) {
      } finally {
        setLoadingTrackLogin(false);
      }
    };

    trackLogin();
  }, []);

  useEffect(() => {
    if (site.auth0_verification_url && site.auth0_access_token_field_name && ssos.length) {
      auth.verifySSOAccessToken(site.auth0_access_token_field_name).then((data) => {
        if (data.status !== 200) {
          // If it is home page and sso relay url exists redirect otherwise login
          (site.sso_default_relay_url && ["/"].includes(window.location.pathname)) ? window.location.href = `${site.sso_default_relay_url}/api/login-sso?RelayState=${window.location.origin}` :
          setLoadingSsos(false)
        }
      })
    }
  }, [site.auth0_verification_url, site.auth0_access_token_field_name, ssos.length])

  const clickWorkspace = async () => {
    try {
      setDisabled(true);
      const { data: resData } = await apiRequest("GET", `/portal/users/workspaces/page`);

      if (resData.status === 200) {
        toast.success("Successfully logged in.");
        navigate(resData.link);
      } else {
        toast.error("No workspace found.");
        navigate("/permission-denied");
      }
    } catch (err) {
      toast.error(err.message);
      navigate("/permission-denied");
    } finally {
      setDisabled(false);
    }
  };

  const redirectToWorkspace = async (userId) => {
    if (!userId) {
      navigate("/permission-denied");
      return;
    }

    try {
      setDisabled(true);
      await clickWorkspace();
    } catch (err) {
      navigate("/permission-denied");
    } finally {
      setDisabled(false);
    }
  };

  useEffect(() => {
    loadSso();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadSso]);

  const onSubmit = async () => {
    if (lockout) return;

    setDisabled(true);

    try {
      const { data: resData } = await apiRequest("post", "/portal/auth-login", {
        body: {
          email,
          password,
        },
      });

      if (resData.status === 200) {
        if (resData.requireTwoFactor) {
          setRequireTwoFactor(true);
        } else {
          localStorage.setItem("accessToken", resData.access_token);
          localStorage.setItem("refreshToken", resData.refresh_token);
          await redirectToWorkspace(resData.userId);
        }
      } else {
        const attemp_count_data = resData?.attemp_count_data || { attemp_count: 0 };

        if (attemp_count_data?.attemp_count > 10) {
          manageLockout(attemp_count_data, intervalId);
        }

        toast.error(resData.message);
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setDisabled(false);
    }
  };

  const verifyOtp = async () => {
    setDisabled(true);

    try {
      const { data: resData } = await apiRequest("post", "/portal/otp/verify", {
        body: {
          email,
          otp,
        },
      });

      if (resData.status === 200) {
        toast.success(resData.message);

        localStorage.setItem("accessToken", resData.access_token);
        localStorage.setItem("refreshToken", resData.refresh_token);
        await redirectToWorkspace(resData.userId);
      } else {
        toast.error(resData.message);
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setDisabled(false);
    }
  };

  const redirectSso = (e) => {
    if (e) {
      e.preventDefault();
    }

    // If auth0 token is stored, then auto login after site is set
    if (
      site?._id && 
      !(site.auth0_verification_url && site.auth0_access_token_field_name) && 
      ssos.length
    ) {
      window.location.href = `${site.sso_default_relay_url}/api/login-sso?RelayState=${window.location.origin}`;
    }
  };

  if (["/"].includes(window.location.pathname) && ssos.length > 0 && loadingSsos && site?.sso_default_relay_url) {
    redirectSso();
  }

  if (["/", "/login", "/login/"].includes(window.location.pathname) && loadingSsos) {
    return (
      <div className="flex h-full items-center justify-center">
        <Preloader />
      </div>
    );
  }

  return (
    <AuthDataLayout
      title={site?.login_page_title}
      subTitle={site?.login_page_subtitle}>
      <form
        className="flex w-full flex-col"
        id="loginform"
        action="/portal/auth-login"
        method="post">
        <div className="flex w-full flex-col gap-y-2">
          {!requireTwoFactor ? (
            <>
              <div className="w-full">
                <Input
                  labelClasses={"font-medium mb-0"}
                  inputValue={email}
                  onChange={(e) => setEmail(e.target.value)}
                  label={"Email"}
                  inputType={"email"}
                  inputName={"email"}
                />
              </div>
              <div className="w-full">
                <Input
                  labelClasses={"font-medium mb-0"}
                  inputValue={password}
                  onChange={(e) => setPassword(e.target.value)}
                  label={"Password"}
                  inputType={"password"}
                  inputName={"password"}
                />
              </div>
            </>
          ) : (
            <div className="w-full">
              <Input
                inputValue={otp}
                onChange={(e) => {
                  if (e.target.value && !/^[0-9]+$/.test(e.target.value)) {
                    return;
                  } else if (e.target.value.length > 6) {
                    return;
                  }
                  setOtp(e.target.value);
                }}
                label={"OTP"}
                inputType={"text"}
                inputName={"otp"}
              />
            </div>
          )}
        </div>
        <div className="flex justify-end">
          <Button
            buttonClasses={"!px-0 !h-auto !bg-transparent !rounded-none !text-sm !my-2"}
            buttonHasLink={true}
            buttonLink={"/forgot-password"}
            buttonLabel={"Forgot Password?"}
            buttonLabelClasses={"!text-sm hover:!underline !text-slate-800 !font-medium"}
          />
        </div>
        <div className="mt-2 grid gap-y-4">
          {!loadingTrackLogin && (
            <Button
              isDisable={disabled || lockout}
              buttonClasses={"w-full"}
              buttonType={"Submit"}
              buttonLabelClasses={" !font-normal "}
              buttonLabel={requireTwoFactor ? "Submit" : !lockout ? "Login" : `Your login has been blocked for ${lockout === 60 ? "01:00" : `00:${lockout < 10 ? "0" : ""}${lockout}`}`}
              buttonFunction={(e) => {
                e.preventDefault();
                !requireTwoFactor ? onSubmit() : verifyOtp();
              }}
              buttonStyles={{
                borderColor: site?.highlight_color,
                backgroundColor: site?.highlight_color,
              }}
            />
          )}
        </div>
        {ssos.length > 0 && (
          <>
            <div className="my-5 flex items-center justify-between">
              <div className="h-[1px] w-1/4 bg-gray-300"></div>
              <span className="text-xs text-gray-500 sm:text-sm">Or continue with SSO</span>
              <div className="h-[1px] w-1/4 bg-gray-300"></div>
            </div>
            {ssos.map((item) => (
              <div
                key={item._id}
                className="mt-4">
                <Button
                  buttonClasses={"w-full"}
                  buttonType={"submit"}
                  buttonLabel={site.sso_button_text ? site.sso_button_text : item.provider_name}
                  buttonLabelClasses={""}
                  buttonStyles={{
                    borderColor: site?.sso_button_text_color,
                    color: site?.sso_button_text_color || "#fff",
                    backgroundColor: site?.sso_button_color,
                  }}
                  buttonFunction={redirectSso}
                />
              </div>
            ))}
          </>
        )}
      </form>
    </AuthDataLayout>
  );
};

export default Login;
